import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Divider, Button
} from '@mui/material';
import * as XLSX from 'xlsx';

// IndexedDB helper functions
const DB_NAME = 'OrdersDB';
const STORE_NAME = 'orders';
const DB_VERSION = 1;

async function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: '_id' });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject('Failed to open DB');
    });
}

async function getAllOrders() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
            resolve(request.result || []);
        };
        request.onerror = () => reject('Failed to get orders');
    });
}

async function deleteOrder(id) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.delete(id);

        request.onsuccess = () => resolve(true);
        request.onerror = () => reject('Failed to delete order');
    });
}

async function clearOrders() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => resolve(true);
        request.onerror = () => reject('Failed to clear orders');
    });
}

// Safely normalize any raw price value
function normalizePrice(rawPrice) {
    if (typeof rawPrice === 'string') {
        const cleaned = rawPrice.replace(/[^0-9.]/g, '');
        return parseFloat(cleaned) || 0;
    }
    return parseFloat(rawPrice) || 0;
}

export const OrdersDashboard = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const STORAGE_LIMIT_APPROX = 4.5 * 1024 * 1024 * 1024; // 4.5 GB

    useEffect(() => {
        const loadOrders = async () => {
            try {
                const data = await getAllOrders();
                setOrders(data);
                setLoading(false);

                const jsonSize = new Blob([JSON.stringify(data)]).size;
                if (jsonSize >= STORAGE_LIMIT_APPROX) {
                    await exportOrdersToExcel(data);
                    await clearOrders();
                    setOrders([]);
                    alert("Storage limit reached. Orders backed up and cleared.");
                }
            } catch (error) {
                console.error('Error loading orders:', error);
                setOrders([]);
                setLoading(false);
            }
        };

        loadOrders();
    }, [STORAGE_LIMIT_APPROX]);

    const exportOrdersToExcel = async (ordersData) => {
        const wb = XLSX.utils.book_new();

        ordersData.forEach((order, index) => {
            const items = order.items.map(item => {
                const price = normalizePrice(item.price);
                const amount = (price * item.quantity).toFixed(2);
                return {
                    Name: item.name,
                    Price: price.toFixed(2),
                    Quantity: item.quantity,
                    Amount: amount
                };
            });

            items.push({
                Name: 'TOTAL',
                Price: '',
                Quantity: '',
                Amount: normalizePrice(order.total).toFixed(2)
            });

            const ws = XLSX.utils.json_to_sheet(items);
            XLSX.utils.book_append_sheet(wb, ws, `Order_${index + 1}_${order._id}`);
        });

        XLSX.writeFile(wb, 'Chickago_Orders_Backup.xlsx');
    };

    const handleDelete = async (id) => {
        try {
            await deleteOrder(id);
            setOrders(prev => prev.filter(order => order._id !== id));
        } catch (err) {
            console.error('Failed to delete order:', err);
        }
    };

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom>Orders Overview</Typography>

            {loading ? (
                <Typography>Loading...</Typography>
            ) : orders.length === 0 ? (
                <Typography>No orders found.</Typography>
            ) : (
                orders.map((order, index) => (
                    <Paper key={order._id} sx={{ mb: 4, p: 2, boxShadow: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">
                                Order #{index + 1} - {new Date(order.timestamp || Date.now()).toLocaleString()}
                            </Typography>
                            <Button variant="outlined" color="error" onClick={() => handleDelete(order._id)}>
                                Delete Order
                            </Button>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Item</strong></TableCell>
                                        <TableCell><strong>Price</strong></TableCell>
                                        <TableCell><strong>Quantity</strong></TableCell>
                                        <TableCell><strong>Amount</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order.items.map((item, idx) => {
                                        const price = normalizePrice(item.price);
                                        const itemTotal = (price * item.quantity).toFixed(2);
                                        return (
                                            <TableRow key={idx}>
                                                <TableCell>{item.name}</TableCell>
                                                <TableCell>Rs {price.toFixed(2)}</TableCell>
                                                <TableCell>{item.quantity}</TableCell>
                                                <TableCell>Rs {itemTotal}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow>
                                        <TableCell colSpan={3}><strong>Order Total</strong></TableCell>
                                        <TableCell><strong>Rs {normalizePrice(order.total).toFixed(2)}</strong></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                ))
            )}
        </Box>
    );
};
